import {useStaffSession} from '../ui/StaffSession';
import {HrbEvidenceIndex} from './HrbEvidenceIndex';
import {PageHeader} from '../ui/PageHeader';
import {useEffect,useState} from 'react';
import {Link,useParams} from 'react-router';
import {api} from '../api';
import {formatDateOnly,formatDateTime} from '../dates';
import {Empty,ErrorState,Loading} from '../ui/State';
export type Evidence={mappingId:string;title:string;documentNumber?:string;versionNumber:number|null;responsibleParty:string;scope:string;reviewDate?:string|null;reviewStatus:string;sourceReference:string;mappingNote?:string;redacted?:boolean;provenance?:{proposedBy:string;reviewedBy:string;reviewedAt:string}};
export type Area={code:string;name:string;sortOrder:number;evidence:Evidence[];escalations:Array<{id:string;title:string;reason_outside_competence:string;status:string;action_number:string}>;gap:string|null};
type Pack={notice:string;building:{id:string;name:string};areas:Area[];recordedGaps:Array<{id:string;title:string;review_date?:string;party_name:string}>;summary:{areaCount:number;coveredAreas:number;gapCount:number;evidenceCount:number;redactionCount:number}};
type ExportResult={content:string;fileName:string};
export function HrbEvidencePack(){const{id=''}=useParams();const session=useStaffSession();const selectedBuilding=session?.effectiveScope.kind==='building'?session.effectiveScope.id:undefined;const[data,setData]=useState<Pack>(),[error,setError]=useState<unknown>(),[exporting,setExporting]=useState(false);useEffect(()=>{const controller=new AbortController();setData(undefined);setError(undefined);if(selectedBuilding&&selectedBuilding!==id){setError(Error('The evidence pack route does not match the selected building.'));return()=>controller.abort()}void api<Pack>(`/api/hrb-evidence-pack/buildings/${id}/preview`,{signal:controller.signal}).then(result=>{if(result.building.id!==id||!Array.isArray(result.areas)||!Array.isArray(result.recordedGaps))throw Error('The evidence pack could not be matched to this building.');if(!controller.signal.aborted)setData(result)}).catch(cause=>{if(!controller.signal.aborted)setError(cause)});return()=>controller.abort()},[id,selectedBuilding]);async function exportPack(format:'JSON'|'CSV'|'Print'){setExporting(true);setError(undefined);try{const result=await api<ExportResult>(`/api/hrb-evidence-pack/buildings/${id}/export`,{method:'POST',body:JSON.stringify({format})});if(format==='Print'){window.print();return}const blob=new Blob([result.content],{type:format==='CSV'?'text/csv':'application/json'}),url=URL.createObjectURL(blob),a=document.createElement('a');a.href=url;a.download=result.fileName;a.click();URL.revokeObjectURL(url)}catch(e){setError(e)}finally{setExporting(false)}}const heading=<PageHeader title="Building safety & evidence packs" context={data?.building.name??'Reviewed repository versions, recorded gaps and escalations.'}
 primaryAction={data?{kind:'button',label:'Print / save PDF',disabled:exporting,onClick:()=>void exportPack('Print')}:undefined}
 secondaryActions={[{kind:'link',label:'Responsibilities and competence evidence',to:`/buildings/${id}/building-safety/competence`},...(data?[{kind:'button' as const,label:'Export CSV',disabled:exporting,onClick:()=>void exportPack('CSV')},{kind:'button' as const,label:'Export JSON',disabled:exporting,onClick:()=>void exportPack('JSON')}]:[])]}/>;
if(error&&!data)return <>{heading}<ErrorState error={error}/>
</>;if(!data)return <>{heading}<Loading/>
</>;
return <div className="hrb-evidence-workspace">{heading}{Boolean(error)&&<ErrorState error={error}/>}<p className="warning governance-boundary">
<strong>Evidence pack — not certification.</strong> {data.notice}</p>
<section className="panel evidence-pack-summary" aria-label="Evidence-pack summary">
<div>
<strong>{data.summary.coveredAreas} of {data.summary.areaCount}</strong>
<span>areas with approved evidence</span>
</div>
<div>
<strong>{data.summary.evidenceCount}</strong>
<span>eligible evidence versions</span>
</div>
<div>
<strong>{data.summary.gapCount}</strong>
<span>area gaps</span>
</div>{data.summary.redactionCount>0&&<div>
<strong>{data.summary.redactionCount}</strong>
<span>restricted entries redacted</span>
</div>}</section>
<HrbEvidenceIndex areas={data.areas}/>
<details className="evidence-pack-source-details">
<summary>Evidence areas, provenance and escalations</summary>
<div className="evidence-pack-areas">{data.areas.map(area=>
<section className="panel" key={area.code} aria-labelledby={`area-${area.code}`}>
<div className="section-heading">
<div>
<span className="eyebrow">Area {area.sortOrder} of 6</span>
<h2 id={`area-${area.code}`} tabIndex={-1}>{area.name}</h2>
</div>
<span>{area.evidence.length} approved</span>
</div>{area.gap&&<p className="warning">
<strong>Gap:</strong> {area.gap}</p>}{area.evidence.length?<div className="governance-list">{area.evidence.map(e=>
<article className="compact-record" key={e.mappingId}>
<div>
<strong>{e.title}</strong>
<span>{e.redacted?'Restricted details withheld':`${e.documentNumber} · immutable version ${e.versionNumber}`}</span>
</div>
<dl className="detail-grid">
<div>
<dt>Responsible</dt>
<dd>{e.responsibleParty}</dd>
</div>
<div>
<dt>Scope</dt>
<dd>{e.scope}</dd>
</div>
<div>
<dt>Review</dt>
<dd>{e.reviewStatus}{e.reviewDate?` · ${formatDateOnly(e.reviewDate)}`:''}</dd>
</div>
<div>
<dt>Source</dt>
<dd>{e.sourceReference}</dd>
</div>
</dl>{e.mappingNote&&<p>{e.mappingNote}</p>}{e.provenance&&<small>Proposed by {e.provenance.proposedBy}; approved by {e.provenance.reviewedBy} on {formatDateTime(e.provenance.reviewedAt)}</small>}</article>)}</div>:<Empty title="No eligible approved evidence" body="Approve a current, verified repository version for this area or record the gap and escalation."/>}{area.escalations.length>0&&<details>
<summary>Specialist escalations ({area.escalations.length})</summary>{area.escalations.map(e=>
<div className="inline-record" key={e.id}>
<span>
<strong>{e.title}</strong>
<small>{e.action_number} · {e.status} · {e.reason_outside_competence}</small>
</span>
</div>)}</details>}</section>)}</div>{data.recordedGaps.length>0&&<section className="panel">
<h2>Recorded competence evidence gaps</h2>{data.recordedGaps.map(g=>
<div className="inline-record" key={g.id}>
<span>
<strong>{g.title}</strong>
<small>{g.party_name}{g.review_date?` · Review ${formatDateOnly(g.review_date)}`:''}</small>
</span>
</div>)}</section>}</details>
<p className="info governance-boundary">
Export receipts record the actor, time and exact snapshot hash without duplicating repository files.</p>
</div>}
